import { forwardRef, useMemo } from "react";
import { Flex, Text, Box } from "rebass/styled-components";
import styled from "styled-components";
import { BiMessageRounded, BiMinusCircle } from "react-icons/bi";

import { ClipCommentForm } from "../ClipCommentForm";
import { TUserGroup, TUserRank } from "MVVM/@types";
import { Avatar } from "MVVM/Components/AvatarV2";
import { IClipComment } from "MVVM/Components/Comments/Clip/@types";
import { CommentTooltip } from "MVVM/Components/Comments/Clip/ClipComment";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { Tooltip } from "MVVM/Components/Tooltip";
import { useUser } from "MVVM/Hooks/useUser";
import { dateFormatWithDistanceSuffix } from "MVVM/Utilities/Clip";
import { randomAvatar } from "MVVM/Utilities/Images";
import { IAllstarUser } from "MVVM/Views/Setup/Components/@types";

const LoadingContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  padding: 16px 0;
`;

export const Top5WidgetComments = forwardRef<
  HTMLDivElement,
  {
    disabled: boolean;
    comments: IClipComment[];
    isLoadingMore: boolean;
    clipId: string;
  }
>(({ disabled, comments, isLoadingMore, clipId }, ref) => {
  const { allstarUser } = useUser();

  return (
    <>
      <ClipCommentsWrapper>
        <ClipCommentsContainer ref={ref}>
          {disabled ? (
            <EmptyList text="Comments are disabled" />
          ) : comments?.length ? (
            <>
              {isLoadingMore && (
                <LoadingContainer>
                  <LoadingSpinner height={20} width={20} />
                </LoadingContainer>
              )}
              {comments.map((comment: IClipComment) => {
                const avatar =
                  comment.user?.avatarUrl || useMemo(randomAvatar, []);
                const username = comment.user?.username || "Allstar Gamer";

                return (
                  <Top5Comment
                    comment={comment}
                    avatar={avatar}
                    username={username}
                    allstarUser={allstarUser}
                  />
                );
              })}
            </>
          ) : (
            <EmptyList text="Start the conversation..." />
          )}
        </ClipCommentsContainer>
      </ClipCommentsWrapper>
      {!disabled && <ClipCommentForm clipId={clipId} />}
    </>
  );
});

const ClipCommentsWrapper = styled(Flex)`
  flex-direction: column;
  height: 100%;
  min-height: 0;
  max-height: 224px;
  overflow: hidden;
  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    flex-direction: column-reverse;
  }
`;

const ClipCommentsContainer = styled(Flex)`
  flex-direction: column;
  flex: 1;
  gap: 8px;
  min-height: 0;
  overflow-y: auto;
  padding: 0;
  scrollbar-width: none;
  width: 100%;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const EmptyList = ({ text }: { text: string }) => {
  return (
    <StyledContainer>
      <StyledIcon />
      <Text color="chalk" fontSize={14} fontWeight={500}>
        {text}
      </Text>
    </StyledContainer>
  );
};

const StyledContainer = styled(Flex)`
  align-items: center;
  background: rgba(30, 46, 58, 0.4);
  backdrop-filter: blur(16px);
  border: solid 1px ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  justify-content: center;
  opacity: 0.6;
  padding: 25px 0px;
`;

const StyledIcon = styled(BiMessageRounded)`
  color: ${({ theme }) => theme.colors.chalk};
  height: 30px;
  width: 30px;
`;

interface ITop5CommentProps {
  comment: IClipComment;
  avatar: string;
  username: string;
  allstarUser: IAllstarUser;
}

const Top5Comment = ({
  comment,
  avatar,
  username,
  allstarUser,
}: ITop5CommentProps) => {
  if (comment.deletedAt)
    return (
      <StyledComment>
        <Flex
          color="chalk"
          style={{ fontStyle: "italic", gap: "4px" }}
          variant="text.paragraphSmall"
          fontWeight={500}
          alignItems="center"
        >
          <StyledBiMinusCircle />
          This comment has been removed.
        </Flex>
      </StyledComment>
    );

  return (
    <StyledTop5Container>
      <Box
        style={{ cursor: "pointer", transform: "scale(1.1)" }}
        pt={2}
        flexShrink={0}
      >
        <Avatar
          profileCardUsername={comment.user?.username}
          userGroup={comment.user.userGroup?.type as TUserGroup}
          rank={comment.user.rank as TUserRank}
          src={avatar}
          size="xs"
          link={`/u/${comment.user?.username || comment.user?._id}`}
        />
      </Box>
      <Flex flexDirection="column" width="100%">
        <Flex>
          <Flex style={{ gap: "4px" }} flexGrow="1">
            <Text color="chalk" variant="text.labelXSmall" fontWeight={500}>
              {username}
            </Text>
            <StyledCreatedAtText variant="text.labelXSmall" fontWeight={500}>
              {dateFormatWithDistanceSuffix(comment.createdAt)}
            </StyledCreatedAtText>
          </Flex>
          {allstarUser.loggedIn && (
            <Tooltip
              tooltipContent={<CommentTooltip comment={comment} />}
              type="click"
              position={{
                right: "18px",
                top: "2px",
              }}
            >
              <Text
                color="chalk"
                variant="text.paragraphMedium"
                fontWeight={500}
              >
                &#8942;
              </Text>
            </Tooltip>
          )}
        </Flex>
        <Text
          color="ice"
          variant="text.paragraphSmall"
          style={{ overflowWrap: "break-word" }}
        >
          {comment.text}
        </Text>
      </Flex>
    </StyledTop5Container>
  );
};

const StyledBiMinusCircle = styled(BiMinusCircle)`
  color: ${({ theme }) => theme.colors.alert};
`;

const StyledComment = styled(Flex)`
  border-radius: 8px;
  cursor: pointer;
  gap: 20px;
  padding: 12px;

  :hover {
    background: ${({ theme }) => theme.colors.navy};
  }
`;

const StyledTop5Container = styled(Flex)`
  background: rgba(30, 46, 58, 0.4);
  backdrop-filter: blur(16px);
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  cursor: pointer;
  gap: 12px;
  padding: 12px;
`;

const StyledCreatedAtText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
`;
